import {
  useCallback,
  useState,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  cn,
  Flex,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Icon,
  Text,
} from '@kognitos/lattice';
import { formatDisplayName } from '@/shared/utils';
import { isDownloadable, isHtmlString } from '@/modules/outputs/utils';
import { useIDPDocumentName } from '@/modules/outputs/hooks';
import {
  EOutputCardSize,
  EOutputCardVariant,
  type IOutputCardProps,
  type TBrowserPreviewStatus,
  type TResolvedValue,
} from '@/modules/outputs/types';
import { OutputCardPreview, OutputDocumentMetadata } from './OutputCardPreview';
import {
  BrowserPreviewStatusBadge,
  BrowserPreviewStatusContext,
} from './BrowserPreviewStatus';
import { OutputGlyph } from './OutputGlyph';
import {
  getOutputCardSummary,
  serializeOutputForCopy,
} from './output-card-utils';
import { getOutputPresentation } from './output-presentation';
import { useIsOverflowing } from '../hooks/useIsOverflowing';
import { useCopyOutput } from '../hooks/useCopyOutput';
import './output-card.css';

/*
 * `output-card.css` keeps only what a class name cannot express: the two
 * inset overflow fades and the glyph marks. Everything
 * static lives here instead, so the sizes read as one set. The `output-card*`
 * names stay on the elements because the glyph hover rules, and a few suites
 * outside this module, still hang off them.
 */
const CARD_CLASSES =
  'output-card group/output-card gap-0 overflow-hidden border border-border bg-card transition-shadow';

/* theme-fixed: the design packet specifies this exact compact-card lift in
   both themes and no shadow token matches it. */
const COMPACT_HOVER_SHADOW =
  'hover:shadow-[0_1px_2px_rgb(0_0_0/8%),0_2px_6px_-2px_rgb(0_0_0/8%)]';

const SMALL_CARD_CLASSES = `output-card--sm relative w-max max-w-[190px] flex-none flex-row items-center gap-2 rounded-[10px] py-[5px] pr-2.5 pl-1.5 ${COMPACT_HOVER_SHADOW}`;

const MEDIUM_CARD_CLASSES = `output-card--md h-[176px] w-[248px] flex-none rounded-xl p-2 ${COMPACT_HOVER_SHADOW}`;

const LARGE_CARD_CLASSES =
  'output-card--lg w-full overflow-visible rounded-none border-0 bg-transparent p-0';

/** The rounded well a preview is drawn in, shared by the md and hover cards. */
const PREVIEW_CLASSES = 'min-h-full rounded-lg bg-muted/50';

/**
 * The lg well. Its tint is mixed against the card rather than layered over it
 * with an alpha, because the lg card itself is transparent and so cannot be
 * relied on to supply the backdrop.
 */
const LARGE_BODY_CLASSES =
  'relative rounded-lg bg-[color-mix(in_oklch,var(--muted)_45%,var(--card))] p-3';

/**
 * The one thing the run assistant genuinely needs of its own: it packs cards
 * two-up in a container grid, so a body is capped in height or a single long
 * output pushes its neighbour off screen. Everything else about the well is
 * the same on both surfaces.
 */
const ASSISTANT_BODY_MAX_HEIGHTS: Partial<Record<EOutputCardVariant, string>> =
  {
    [EOutputCardVariant.TEXT]: 'max-h-[200px]',
    [EOutputCardVariant.CODE]: 'max-h-[200px]',
    [EOutputCardVariant.DICT]: 'max-h-[200px]',
    [EOutputCardVariant.LIST]: 'max-h-[200px]',
    [EOutputCardVariant.TABLE]: 'max-h-[240px]',
    [EOutputCardVariant.BROWSER]: 'max-h-[240px]',
    [EOutputCardVariant.FILE]: 'max-h-[320px]',
    [EOutputCardVariant.IDP]: 'max-h-[320px]',
  };

/**
 * Variants that draw their own header, page or chrome. The well hands them the
 * whole box rather than framing something that is already framed.
 */
const SELF_FRAMED_VARIANTS: readonly EOutputCardVariant[] = [
  EOutputCardVariant.TABLE,
  EOutputCardVariant.BROWSER,
  EOutputCardVariant.FILE,
  EOutputCardVariant.IDP,
];

/**
 * A document or a rendered page sits flush in the well, so it keeps a hairline
 * edge of its own to read as a sheet rather than a bleed. The ring is the 1px
 * inset border; the shadow is theme-fixed, as the design packet specifies.
 */
const SHEET_EDGE_CLASSES =
  'shadow-[0_1px_2px_rgb(0_0_0/8%)] ring-1 ring-border ring-inset';

/**
 * Reveal-on-hover for the card's action row. Tailwind
 * scopes `hover` to pointer devices, so touch gets the controls outright.
 */
const HOVER_REVEAL_CLASSES =
  'opacity-0 transition-opacity duration-150 group-hover/output-card:opacity-100 group-focus-within/output-card:opacity-100 [@media(hover:none)]:opacity-100';

/**
 * Which surface is rendering the card. Declared here rather than on
 * `IOutputCardProps` because only the run assistant opts in, and it does so
 * from `RunAssistantOutput` rather than from its own callers.
 */
export type TOutputCardSurface = 'default' | 'assistant';

interface IOutputCardComponentProps extends IOutputCardProps {
  surface?: TOutputCardSurface;
}

interface ICardActionButtonProps {
  label: string;
  icon: 'Download' | 'Copy' | 'Maximize';
  onClick: () => void;
  size: EOutputCardSize;
  disabled?: boolean;
}

function CardActionButton({
  label,
  icon,
  onClick,
  size,
  disabled = false,
}: ICardActionButtonProps) {
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClick();
  };
  return (
    <Button
      variant="ghost"
      size={size === EOutputCardSize.MD ? 'icon-xs' : 'icon-sm'}
      aria-label={label}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        'shrink-0 border-none text-muted-foreground focus-visible:ring-0',
        HOVER_REVEAL_CLASSES,
        // Hidden controls must not swallow clicks aimed at the card beneath.
        'pointer-events-none group-hover/output-card:pointer-events-auto group-focus-within/output-card:pointer-events-auto [@media(hover:none)]:pointer-events-auto',
      )}
    >
      <Icon type={icon} size="xs" />
    </Button>
  );
}

interface ICardActionsProps {
  size: EOutputCardSize.MD | EOutputCardSize.LG;
  onDownload?: () => void;
  onCopy?: () => void;
  onExpand?: () => void;
  className?: string;
}

function CardActions({
  size,
  onDownload,
  onCopy,
  onExpand,
  className,
}: ICardActionsProps) {
  const { t } = useTranslation('outputs');
  return (
    <Flex
      align="center"
      gap={0.5}
      className={cn('ml-auto shrink-0', className)}
    >
      {onDownload && (
        <CardActionButton
          size={size}
          icon="Download"
          label={t('outputs.actions.download')}
          onClick={onDownload}
        />
      )}
      {size === EOutputCardSize.LG && onCopy && (
        <CardActionButton
          size={size}
          icon="Copy"
          label={t('outputs.card.copy')}
          onClick={onCopy}
        />
      )}
      {onExpand && (
        <CardActionButton
          size={size}
          icon="Maximize"
          label={t('outputs.actions.expand')}
          onClick={onExpand}
        />
      )}
    </Flex>
  );
}

interface IMediumHeaderProps {
  label: string;
  variant: EOutputCardVariant;
  showIcon: boolean;
  loading: boolean;
  actions?: ICardActionsProps;
}

function MediumHeader({
  label,
  variant,
  showIcon,
  loading,
  actions,
}: IMediumHeaderProps) {
  return (
    <Flex align="center" gap={2} className="h-6 min-h-6 min-w-0 shrink-0">
      {showIcon && (
        <OutputGlyph
          variant={variant}
          size={EOutputCardSize.MD}
          loading={loading}
        />
      )}
      <Text
        level="xSmall"
        color="muted"
        weight="normal"
        className="min-w-0 flex-1 truncate"
        data-testid="output-card-title"
      >
        {label}
      </Text>
      {actions && <CardActions {...actions} />}
    </Flex>
  );
}

interface ISmallCardProps {
  label: string;
  summary: string;
  variant: EOutputCardVariant;
  value: TResolvedValue;
  outputName: string;
  showIcon: boolean;
  loading: boolean;
  clipped: boolean;
  onExpand?: () => void;
  onNestedExpand?: (name: string, value: TResolvedValue) => void;
}

function SmallCard({
  label,
  summary,
  variant,
  value,
  outputName,
  showIcon,
  loading,
  clipped,
  onExpand,
  onNestedExpand,
}: ISmallCardProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (!onExpand || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onExpand();
  };
  const card = (
    <Card
      className={cn(CARD_CLASSES, SMALL_CARD_CLASSES)}
      onClick={onExpand}
      onKeyDown={handleKeyDown}
      role={onExpand ? 'button' : undefined}
      tabIndex={onExpand ? 0 : undefined}
      data-size="sm"
    >
      {showIcon && (
        <OutputGlyph
          variant={variant}
          size={EOutputCardSize.SM}
          loading={loading}
        />
      )}
      <Flex direction="column" className="min-w-0 flex-1">
        <Text
          color="muted"
          weight="normal"
          className="truncate text-[10px] leading-[13px]"
          data-testid="output-card-title"
        >
          {label}
        </Text>
        <Text
          weight="normal"
          className="truncate text-xs leading-4 [font-variant-numeric:tabular-nums]"
          data-testid="output-card-value"
        >
          {summary}
        </Text>
      </Flex>
    </Card>
  );

  if (!clipped) return card;
  return (
    <HoverCard openDelay={100} closeDelay={0}>
      <HoverCardTrigger asChild>{card}</HoverCardTrigger>
      <HoverCardContent
        side="top"
        sideOffset={6}
        align="start"
        collisionPadding={8}
        className="pointer-events-none max-h-[266px] w-(--radix-hover-card-trigger-width) overflow-hidden rounded-xl border-border bg-card p-2 shadow-sm"
        data-testid="output-card-hover-preview"
      >
        <Flex direction="column" gap={2} className="min-w-0">
          <MediumHeader
            label={label}
            variant={variant}
            showIcon={showIcon}
            loading={loading}
          />
          <Flex
            direction="column"
            align="stretch"
            position="relative"
            overflow="hidden"
            // The hover card only exists because the summary is clipped, so the
            // bottom fade is unconditional here.
            className="output-card__overflow-shadow max-h-[200px] rounded-lg"
          >
            <Flex
              direction="column"
              align="stretch"
              className={PREVIEW_CLASSES}
            >
              <OutputCardPreview
                value={value}
                variant={variant}
                size={EOutputCardSize.MD}
                outputName={outputName}
                onExpand={onNestedExpand}
              />
            </Flex>
          </Flex>
        </Flex>
      </HoverCardContent>
    </HoverCard>
  );
}

interface IMediumCardProps
  extends Omit<ISmallCardProps, 'summary' | 'clipped'> {
  clipped: boolean;
  actions: ICardActionsProps;
}

function MediumCard({
  label,
  variant,
  value,
  outputName,
  showIcon,
  loading,
  clipped,
  onExpand,
  onNestedExpand,
  actions,
}: IMediumCardProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (!onExpand || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onExpand();
  };
  return (
    <Card
      className={cn(CARD_CLASSES, MEDIUM_CARD_CLASSES)}
      onClick={onExpand}
      onKeyDown={handleKeyDown}
      role={onExpand ? 'button' : undefined}
      tabIndex={onExpand ? 0 : undefined}
      data-size="md"
      data-testid="output-card"
    >
      <CardContent className="flex h-full min-h-0 flex-col gap-2 p-0">
        <MediumHeader
          label={label}
          variant={variant}
          showIcon={showIcon}
          loading={loading}
          actions={actions}
        />
        <Flex
          direction="column"
          align="stretch"
          position="relative"
          flex="1"
          overflow="hidden"
          className={cn(
            'min-h-0 rounded-lg',
            clipped && 'output-card__overflow-shadow',
          )}
        >
          {/* The rendered value. Named so the E2E suite can read an
              automation's output the same way it did before the rehaul, when
              every surface went through OutputCardWrapper. */}
          <Flex
            direction="column"
            align="stretch"
            className={PREVIEW_CLASSES}
            data-testid="output-card-value"
          >
            <OutputCardPreview
              value={value}
              variant={variant}
              size={EOutputCardSize.MD}
              outputName={outputName}
              onExpand={onNestedExpand}
            />
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
}

interface ILargeCardProps extends Omit<ISmallCardProps, 'summary' | 'clipped'> {
  showTitle: boolean;
  actions: ICardActionsProps;
  headerActions?: ReactNode;
  headerProps?: HTMLAttributes<HTMLDivElement>;
  scrollAreaRef: (element: HTMLDivElement | null) => void;
  scrollParent: HTMLElement | null;
  clipped: boolean;
  surface: TOutputCardSurface;
  /** Short enough to sit half-width in the run assistant's grid. */
  compact: boolean;
  /** The value is an HTML document, which draws its own page. */
  html: boolean;
}

function LargeCard({
  label,
  variant,
  value,
  outputName,
  showIcon,
  showTitle,
  loading,
  actions,
  headerActions,
  headerProps,
  scrollAreaRef,
  scrollParent,
  clipped,
  surface,
  compact,
  html,
  onExpand,
  onNestedExpand,
}: ILargeCardProps) {
  const { t } = useTranslation('outputs');
  const [browserStatus, setBrowserStatus] =
    useState<TBrowserPreviewStatus>(null);
  const isAssistant = surface === 'assistant';
  const isDocument =
    variant === EOutputCardVariant.FILE || variant === EOutputCardVariant.IDP;
  const hasActions = Boolean(
    actions.onDownload || actions.onCopy || actions.onExpand || headerActions,
  );
  // Only the card's own body responds. A click that started on a control
  // inside the preview -- a nested table button, a link, a media player -- is
  // that control's, not a request to open the whole output.
  const handleBodyClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onExpand) return;
    if (
      event.target instanceof Element &&
      event.target.closest(
        'a, button, input, select, textarea, [role="button"]',
      ) !== event.currentTarget
    ) {
      return;
    }
    onExpand();
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (!onExpand || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onExpand();
  };
  return (
    <Card
      className={cn(
        CARD_CLASSES,
        LARGE_CARD_CLASSES,
        // A document preview is the one lg output that reads better narrow.
        isDocument && 'w-[min(460px,100%)]',
      )}
      data-size="lg"
      data-testid="output-card"
    >
      {/* The actions are not the title's business: a surface that draws its own
          label (showTitle={false}) and no glyph was silently losing download,
          copy and expand along with the header. */}
      {(showIcon || showTitle || hasActions) && (
        <Flex
          align="center"
          gap={2.5}
          {...headerProps}
          className={cn('mb-1.5 min-h-5 min-w-0', headerProps?.className)}
        >
          {showIcon && (
            <OutputGlyph
              variant={variant}
              size={EOutputCardSize.LG}
              loading={loading}
            />
          )}
          {showTitle && (
            <Text
              level="xSmall"
              color="muted"
              weight="normal"
              className="min-w-0 truncate"
              data-testid="output-card-title"
            >
              {label}
            </Text>
          )}
          <BrowserPreviewStatusBadge status={browserStatus} />
          <CardActions {...actions} />
          {headerActions}
        </Flex>
      )}
      <div
        ref={scrollAreaRef}
        className={cn(
          LARGE_BODY_CLASSES,
          // Anything that brings its own frame gets the whole box, and an HTML
          // output brings its own page.
          (SELF_FRAMED_VARIANTS.includes(variant) || html) && 'p-0',
          (variant === EOutputCardVariant.FILE || html) && SHEET_EDGE_CLASSES,
          // The PDF renderer's error state is several components below this
          // one, so the flush file well is the only place to give it room.
          variant === EOutputCardVariant.FILE &&
            '[&_[data-testid=pdf-error-state]]:min-h-11 [&_[data-testid=pdf-error-state]]:py-3',
          isAssistant && 'overflow-hidden',
          isAssistant && ASSISTANT_BODY_MAX_HEIGHTS[variant],
          // A lone scalar would otherwise collapse to a single line of text
          // and leave its grid cell looking empty.
          isAssistant && compact && 'min-h-16',
          clipped && 'output-card__overflow-shadow',
          onExpand &&
            'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
        data-testid="output-card-value"
        onClick={handleBodyClick}
        onKeyDown={handleKeyDown}
        role={onExpand ? 'button' : undefined}
        tabIndex={onExpand ? 0 : undefined}
        aria-label={
          onExpand ? t('outputs.card.openPreview', { name: label }) : undefined
        }
      >
        <BrowserPreviewStatusContext value={setBrowserStatus}>
          <OutputCardPreview
            value={value}
            variant={variant}
            size={EOutputCardSize.LG}
            outputName={outputName}
            scrollParent={scrollParent}
            onExpand={onNestedExpand}
          />
        </BrowserPreviewStatusContext>
        {isDocument && (
          <OutputDocumentMetadata value={value} variant={variant} />
        )}
      </div>
    </Card>
  );
}

/**
 * Renders a resolved output using the surface-driven sm/md/lg card system.
 * Expansion remains an event supplied by the caller; modal/layover internals
 * intentionally stay outside this component.
 */
export function OutputCard({
  name,
  value,
  size,
  onExpand,
  onDownload,
  displayName,
  showIcon = true,
  showTitle = true,
  onCopy,
  headerActions,
  headerProps,
  surface = 'default',
}: IOutputCardComponentProps) {
  const copyOutput = useCopyOutput();
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null);
  const isPreviewOverflowing = useIsOverflowing(scrollParent);
  const presentation = getOutputPresentation(value);
  const { variant, loading, clipped, copyable, compactInAssistant } =
    presentation;
  const cardSize = size ?? EOutputCardSize.MD;

  const idpDocument = useIDPDocumentName(value);
  const isIDP = idpDocument !== null;
  const label =
    displayName ?? idpDocument?.displayName ?? formatDisplayName(name);
  const summary = getOutputCardSummary(value, variant);

  const handleExpand = useCallback(() => {
    if (!loading) onExpand?.(name, value);
  }, [loading, name, onExpand, value]);
  // An empty output has nothing to open, so it is not offered as clickable.
  const expandHandler =
    loading || !onExpand || variant === EOutputCardVariant.EMPTY
      ? undefined
      : handleExpand;

  const handleDownload = useCallback(() => {
    if (idpDocument) {
      void idpDocument.download();
      return;
    }
    onDownload?.(name, value);
  }, [idpDocument, name, onDownload, value]);
  const downloadHandler =
    isIDP || (onDownload && isDownloadable(value)) ? handleDownload : undefined;

  const handleCopy = useCallback(async () => {
    if (onCopy) {
      onCopy(name, value, serializeOutputForCopy(value, variant));
      return;
    }
    await copyOutput(value, variant);
  }, [copyOutput, name, onCopy, value, variant]);

  const actions: ICardActionsProps = {
    size:
      cardSize === EOutputCardSize.LG ? EOutputCardSize.LG : EOutputCardSize.MD,
    onDownload: downloadHandler,
    onCopy:
      copyable && cardSize === EOutputCardSize.LG ? handleCopy : undefined,
    onExpand: expandHandler,
  };

  if (cardSize === EOutputCardSize.SM) {
    return (
      <SmallCard
        label={label}
        summary={summary}
        variant={variant}
        value={value}
        outputName={name}
        showIcon={showIcon}
        loading={loading}
        clipped={clipped}
        onExpand={expandHandler}
        onNestedExpand={onExpand}
      />
    );
  }
  if (cardSize === EOutputCardSize.MD) {
    return (
      <MediumCard
        label={label}
        variant={variant}
        value={value}
        outputName={name}
        showIcon={showIcon}
        loading={loading}
        clipped={clipped}
        onExpand={expandHandler}
        onNestedExpand={onExpand}
        actions={actions}
      />
    );
  }
  return (
    <LargeCard
      label={label}
      variant={variant}
      value={value}
      outputName={name}
      showIcon={showIcon}
      showTitle={showTitle}
      loading={loading}
      onExpand={expandHandler}
      actions={actions}
      headerActions={headerActions}
      headerProps={headerProps}
      scrollAreaRef={setScrollParent}
      scrollParent={scrollParent}
      clipped={isPreviewOverflowing}
      surface={surface}
      compact={compactInAssistant}
      html={isHtmlString(value)}
      onNestedExpand={onExpand}
    />
  );
}
